/**
 * @Author: Junaid Atari junaid.attari@invozone.dev
 * @Date: 2025-02-17 16:54:27
 */

import React from 'react';

import {Formik, Form, FormikHelpers} from 'formik';

// ui
import {Button, Card, Divider, FormLayout, Page, Text, Toast, Frame} from '@shopify/polaris';

// clients
import ApiClient from '~/client/ApiClient';

// custom-components
import DropdownField from '~/ui/form/DropdownField';
import TextInputField from '~/ui/form/TextInputField';
import PageSkeleton from '~/pages/common/PageSkeleton';

// helpers
import {getCurrentHandle, getCurrentStepInfo} from '~/helpers/shopify';

// utils
import {fetchFieldsData} from '~/handles/self-mailer/form-fields-data';
import {schema, type SelfMailerProps} from '~/handles/self-mailer/general';
import {splitFieldsIntoColumns, EXCLUDED_FIELDS, EXCLUDED_CATEGORIES, castFormValues} from '~/helpers/form-fields';

// types
import type {InferType} from 'yup';
import type {FieldInfo} from '~/types/field.types';
import type {SelfMailerFields} from '~/handles/self-mailer/fields-info';
import type {FormFieldsManagerInstance} from '~/classes/FormFieldsManager';

// styles
import '~/styles/global.scss';
import {parseAxiosError} from '~/helpers/request';
import {arrayToDropdownList} from '~/helpers/array';

// open types
type FormValues = InferType<typeof schema>;

const SelfMailer: React.FC<React.PropsWithoutRef<SelfMailerProps>> = (props) => {
  const apiClient = new ApiClient();

  const [fieldsManager, setFieldsManager] = React.useState<FormFieldsManagerInstance>({} as FormFieldsManagerInstance);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [initialValues, setInitialValues] = React.useState<FormValues>({});
  const [toast, setToast] = React.useState<{ message: string; isError: boolean; isActive: boolean }>({
    message: '',
    isError: false,
    isActive: false,
  });

  // Function to toggle Toast
  const toggleToastActive = React.useCallback(() => setToast((prev) => ({...prev, isActive: !prev.isActive})), []);

  // Toast Markup
  const toastMarkup = toast.isActive ? (
    <Toast content={toast.message} onDismiss={toggleToastActive} duration={5000} error={toast.isError}/>
  ) : null;

  const onSubmitHandler = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    const normalizedValues = fieldsManager.normalizeValues(values);

    const template = fieldsManager.getResponses?.()?.[0]?.find?.(c => {
      return +c.id === normalizedValues.select_template;
    });

    if ((template?.meta?.rosEnabled ?? false) && !normalizedValues.offer_percentage) {
      formikHelpers.setErrors({offer_percentage: 'Property\'s Offer Percentage is required'})
      return;
    }

    if (normalizedValues.offer_percentage && (normalizedValues.offer_percentage < 0 || normalizedValues.offer_percentage > 100)) {
      formikHelpers.setErrors({offer_percentage: 'Offer percentage must be number between 0 and 100'});
      return;
    }

    if (normalizedValues.select_date === 'Specific date' && !normalizedValues.specific_date) {
      formikHelpers.setErrors({specific_date: 'Specific date cannot be empty'})
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.put('send-self-mailer/save-data', {
        handle: getCurrentHandle(),
        stepReference: getCurrentStepInfo().stepReference,
        data: normalizedValues,
      });
      props?.onComplete?.(normalizedValues);
    } catch (e) {
      setToast({
        message: 'Error while saving: ' + parseAxiosError(e),
        isError: true,
        isActive: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      const manager = await fetchFieldsData(apiClient);

      setFieldsManager(manager);
      let _initialValues: FormValues = manager.getInitialValues();

      try {
        const response = await apiClient.post('send-self-mailer/retrieve-data', {
          handle: getCurrentHandle(),
          stepReference: getCurrentStepInfo().stepReference,
        });
        _initialValues = {..._initialValues, ...response.data.data};
      } catch {
        // Do nothing
      }

      setInitialValues(castFormValues(_initialValues));

      // set once everything is done
      setIsLoading(false);
      setIsInitialized(true);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isInitialized) return <PageSkeleton/>;

  const finalizedFields: FieldInfo<keyof SelfMailerFields>[] = fieldsManager
    .getFilteredFields(EXCLUDED_FIELDS, EXCLUDED_CATEGORIES);

  const productInfo: Record<string, any> = fieldsManager.getResponses()?.['2'];

  return (
    <Frame>
      {toastMarkup}
      <div style={{marginBottom: 60}}>
        <Page>
          <div className="form-header">
            <h2>Send Self Mailer</h2>
            <h4>Create and mail a self mailer</h4>
          </div>
          <Card>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={schema} validateOnMount validateOnBlur validateOnChange
              onSubmit={onSubmitHandler}>
              {formik => (
                <Form>
                  <FormLayout>
                    <FormLayout.Group>
                    {/* Select templates */}
                    <DropdownField fieldInfo={fieldsManager.findByKeyRequired('select_template')} formik={formik}/>
                      {/* Select postage type */}
                      <DropdownField
                        fieldInfo={fieldsManager.findByKeyRequired('postage_type')}
                        items={arrayToDropdownList(productInfo?.[0]?.deliveryType ?? [])} formik={formik}/>
                    </FormLayout.Group>

                    <FormLayout.Group>
                      {/* Select date */}
                      <div style={{width: selectedDate === 'Specific date' ? '100%' : '49.5%'}}>
                        <DropdownField
                          afterChange={x => {
                            setSelectedDate(x as string);
                            if ( x !== 'Specific date') {
                              formik.setFieldValue('specific_date', '');
                            }
                          }}
                          fieldInfo={fieldsManager.findByKeyRequired('select_date')} formik={formik}/>
                      </div>

                      {/* Specific date */}
                      {selectedDate === 'Specific date' && (
                        <TextInputField
                          textFieldProps={{type: 'date'}}
                          fieldInfo={fieldsManager.findByKeyRequired('specific_date')} formik={formik}/>
                      )}
                    </FormLayout.Group>

                    <div style={{marginTop: 7}}>
                      <Divider borderColor="border"/>
                    </div>

                    <Text as="h2" variant="headingMd" fontWeight="medium">
                      Recipient Information
                    </Text>

                    <FormLayout.Group>
                      <div>
                        <TextInputField fieldInfo={fieldsManager.findByKeyRequired('first_name')} formik={formik}/>
                      </div>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('last_name')} formik={formik}/>
                    </FormLayout.Group>

                    <TextInputField fieldInfo={fieldsManager.findByKeyRequired('company_name')} formik={formik}/>

                    <FormLayout.Group>
                      <div>
                        <TextInputField fieldInfo={fieldsManager.findByKeyRequired('phone_no')} formik={formik}/>
                      </div>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('email')} formik={formik}/>
                    </FormLayout.Group>

                    <Divider borderColor="border"/>

                    <TextInputField fieldInfo={fieldsManager.findByKeyRequired('address_1')} formik={formik}/>
                    <TextInputField fieldInfo={fieldsManager.findByKeyRequired('address_2')} formik={formik}/>

                    <FormLayout.Group>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('city')} formik={formik}/>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('state')} formik={formik}/>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('zip')} formik={formik}/>
                    </FormLayout.Group>

                    {/* <editor-fold desc="Property address fields"> */}
                    {fieldsManager.generalSettings.propertyAddressStrictness === 'Include' && (
                      <>
                        <div style={{marginTop: 7}}>
                          <Divider borderColor="border"/>
                        </div>

                        <div style={{marginTop: 13, marginBottom: 15}}>
                          <Text as="h2" variant="headingMd" fontWeight="medium">
                            Property Address Information
                          </Text>
                        </div>

                        <div style={{marginBottom: 15}}>
                          <TextInputField fieldInfo={fieldsManager.findByKeyRequired('property_address')} formik={formik}/>
                        </div>
                        <div style={{marginBottom: 15}}>
                          <FormLayout.Group>
                            <TextInputField fieldInfo={fieldsManager.findByKeyRequired('property_city')} formik={formik}/>
                            <TextInputField fieldInfo={fieldsManager.findByKeyRequired('property_state')} formik={formik}/>
                          </FormLayout.Group>
                        </div>

                        <FormLayout.Group>
                          <TextInputField fieldInfo={fieldsManager.findByKeyRequired('property_zip_code')} formik={formik}/>

                          <TextInputField
                            fieldInfo={fieldsManager.findByKeyRequired('offer_percentage')} formik={formik}
                            textFieldProps={{min: 0, max: 100, type: 'integer'}}
                          />
                        </FormLayout.Group>
                      </>                    )}
                    {/* </editor-fold> */}

                    <div style={{marginTop: 7}}>
                      <Divider borderColor="border"/>
                    </div>

                    <Text as="h2" variant="headingMd" fontWeight="medium">
                      Sender Information
                    </Text>

                    <FormLayout.Group>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_name')} formik={formik}/>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_company')} formik={formik}/>
                    </FormLayout.Group>

                    <Divider borderColor="border"/>

                    <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_address_1')} formik={formik}/>
                    <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_address_2')} formik={formik}/>

                    <FormLayout.Group>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_city')} formik={formik}/>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_state')} formik={formik}/>
                      <TextInputField fieldInfo={fieldsManager.findByKeyRequired('from_zip')} formik={formik}/>
                    </FormLayout.Group>

                    {finalizedFields
                      .filter(x => !['email', 'phone_no', 'specific_date', 'postage_type'].includes(x.key.toString()))
                      .map(field => {
                        return field.type === 'list' ? (
                          <DropdownField
                            key={`field_key_${field.key}`} items={field?.items ?? []} formik={formik}
                            fieldInfo={field}/>
                        ) : (
                          <TextInputField key={`field_key_${field.key}`} formik={formik} fieldInfo={field}/>
                        );
                      })}

                    {fieldsManager.hasCategory('customField') && (
                      <>
                        <div style={{marginTop: 7, marginBottom: 15}}>
                          <Divider borderColor="border"/>
                        </div>

                        <div style={{marginBottom: 15}}>
                          <Text as="h2" variant="headingMd" fontWeight="medium">
                            Custom Fields
                          </Text>
                        </div>

                        {splitFieldsIntoColumns(fieldsManager
                          .findByCategory('customField'))
                          .map((fields, idx) => (
                            <div key={`column_${idx}`} style={{marginBottom: 10,
                              width: fields.length % 2 === 1 ? '49.5%' : 'auto'
                            }}>
                              <FormLayout.Group>
                                {fields.map(field => (
                                  <TextInputField key={`other_fields_key_${field.key}`} formik={formik}
                                                  fieldInfo={field}/>
                                ))}
                              </FormLayout.Group>
                            </div>
                          ))}
                      </>
                    )}
                    <Divider/>
                    <Button loading={isLoading} disabled={isLoading} submit>Save and Go back to Flow</Button>
                  </FormLayout>
                </Form>
              )}
            </Formik>
          </Card>
        </Page>
      </div>
    </Frame>
  );
};

export default SelfMailer;
